import { useEffect, useState, useCallback } from "react";
import { Newspaper, Plus, Pin, PinOff, Trash2, Loader2, ExternalLink } from "lucide-react";
import { api, useAuth, formatApiError, canDo } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../components/ui/select";
import { Checkbox } from "../components/ui/checkbox";
import ActusPanel from "../components/ActusPanel";
import { toast } from "sonner";

const EMPTY = { titre: "", contenu: "", lien: "", categorie: "general", epingle: false };
const CAT_LABELS = { general: "Général", sst: "SST", ssiap: "SSIAP", carte_pro: "Cartes Pro" };

export default function Actualites() {
  const { user } = useAuth();
  const isAdmin = canDo(user, "actualites", "publier");
  const [annonces, setAnnonces] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [reload, setReload] = useState(0);

  const load = useCallback(() => {
    api.get("/actualites").then((r) => setAnnonces(r.data.annonces)).catch((e) => toast.error(formatApiError(e)));
  }, []);

  useEffect(() => { load(); }, [load]);

  const refresh = () => { load(); setReload((x) => x + 1); };

  const publier = async () => {
    if (!form.titre.trim()) {
      toast.error("Titre requis");
      return;
    }
    setSaving(true);
    try {
      await api.post("/actualites", { ...form, titre: form.titre.trim(), contenu: form.contenu.trim(), lien: form.lien.trim() });
      toast.success("Annonce publiée");
      setForm(EMPTY);
      refresh();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const togglePin = async (a) => {
    try {
      await api.put(`/actualites/${a.id}`, { titre: a.titre, contenu: a.contenu, lien: a.lien, categorie: a.categorie, epingle: !a.epingle });
      refresh();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const supprimer = async (a) => {
    if (!window.confirm(`Supprimer l'annonce "${a.titre}" ?`)) return;
    try {
      await api.delete(`/actualites/${a.id}`);
      toast.success("Annonce supprimée");
      refresh();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div className="space-y-6" data-testid="actualites-page">
      <div>
        <p className="text-xs font-mono uppercase tracking-widest text-accent mb-1">Veille</p>
        <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight flex items-center gap-3" style={{ fontFamily: "Outfit" }}>
          <Newspaper className="h-8 w-8 text-accent" /> Actualités
        </h1>
        <p className="text-sm text-muted-foreground mt-1">Annonces internes épinglées et veille réglementaire officielle (INRS, CNAPS, Ministère de l'Intérieur).</p>
      </div>

      {isAdmin && (
        <div className="rounded-xl border border-border bg-card p-5 space-y-4" data-testid="annonce-form-card">
          <h2 className="text-lg font-bold flex items-center gap-2" style={{ fontFamily: "Outfit" }}>
            <Plus className="h-4 w-4 text-accent" /> Publier une annonce
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="space-y-2 sm:col-span-2">
              <Label>Titre *</Label>
              <Input value={form.titre} onChange={(e) => setForm({ ...form, titre: e.target.value })} className="bg-background" placeholder="Ex : Session MAC prévue le 15/10" data-testid="annonce-titre" />
            </div>
            <div className="space-y-2 sm:col-span-2">
              <Label>Message <span className="text-muted-foreground text-xs">(optionnel)</span></Label>
              <textarea value={form.contenu} onChange={(e) => setForm({ ...form, contenu: e.target.value })} rows={2}
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-ring"
                placeholder="Détails de l'annonce..." data-testid="annonce-contenu" />
            </div>
            <div className="space-y-2">
              <Label>Lien https <span className="text-muted-foreground text-xs">(optionnel)</span></Label>
              <Input value={form.lien} onChange={(e) => setForm({ ...form, lien: e.target.value })} className="bg-background font-mono text-xs" placeholder="https://..." data-testid="annonce-lien" />
            </div>
            <div className="space-y-2">
              <Label>Catégorie</Label>
              <Select value={form.categorie} onValueChange={(v) => setForm({ ...form, categorie: v })}>
                <SelectTrigger className="bg-background" data-testid="annonce-categorie"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(CAT_LABELS).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="flex items-center justify-between flex-wrap gap-3">
            <label className="flex items-center gap-2.5 cursor-pointer">
              <Checkbox checked={form.epingle} onCheckedChange={(v) => setForm({ ...form, epingle: !!v })} data-testid="annonce-epingle" />
              <span className="text-sm">Épingler en haut du tableau de bord</span>
            </label>
            <Button onClick={publier} disabled={saving || !form.titre.trim()} data-testid="annonce-publier">
              {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Plus className="h-4 w-4 mr-2" />}
              Publier
            </Button>
          </div>
        </div>
      )}

      {isAdmin && annonces.length > 0 && (
        <div className="rounded-xl border border-border bg-card p-5 space-y-2" data-testid="annonces-admin-list">
          <h2 className="text-lg font-bold mb-2" style={{ fontFamily: "Outfit" }}>Annonces publiées ({annonces.length})</h2>
          {annonces.map((a) => (
            <div key={a.id} className="flex items-center justify-between gap-2 rounded-lg border border-border px-3 py-2.5" data-testid={`annonce-admin-${a.id}`}>
              <div className="min-w-0">
                <p className="text-sm font-medium truncate flex items-center gap-2">
                  {a.titre}
                  {a.epingle && <Pin className="h-3.5 w-3.5 text-accent shrink-0" />}
                  {a.lien && <ExternalLink className="h-3 w-3 text-muted-foreground shrink-0" />}
                </p>
                <p className="text-xs text-muted-foreground">{CAT_LABELS[a.categorie] || a.categorie} · {a.created_at?.slice(0, 10)}</p>
              </div>
              <div className="flex items-center shrink-0">
                <Button variant="ghost" size="icon" title={a.epingle ? "Désépingler" : "Épingler"} onClick={() => togglePin(a)} data-testid={`annonce-pin-${a.id}`}>
                  {a.epingle ? <PinOff className="h-4 w-4" /> : <Pin className="h-4 w-4" />}
                </Button>
                <Button variant="ghost" size="icon" className="hover:text-red-400" onClick={() => supprimer(a)} data-testid={`annonce-del-${a.id}`}>
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      <ActusPanel key={reload} />
    </div>
  );
}
